import { LANDING_PATHS } from "@/lib/landingPaths";
import { DASHBOARD_ROUTES } from "@/lib/dashboardRoutes";
import { WEBSITE_LEAD_INTERCEPTOR_COPY } from "@/constants/websiteLeadInterceptor";
import { resolveRootLandingKey, getHostname, getSiteBrand } from "@/lib/siteHosts";
import { getStoredLeadId } from "@/lib/leadIdCookie";
import { normalizeHostname } from "./brandConfig";
import rootShellCopy from "./rootShellCopy";
import brandShell from "./brandShell";

const { ROOT_SHELL_COPY, STUB_SHELL_COPY } = rootShellCopy;
const { absoluteBrandUrl, buildBrandJsonLd } = brandShell;

const DEFAULT_META = {
  ...ROOT_SHELL_COPY,
  ogImage: "/images/logo.png",
  robots: "index, follow",
};

const ROOFING_META = {
  title: "Roofing Interceptor | Digital Assistant for Roofers — FractoLabs",
  description:
    "AI digital employee for roofing teams: reply in 9 seconds, qualify leak and inspection leads, and book appointments 24/7. Works with your CRM. 14-day free trial.",
  ogTitle: "Roofing Interceptor | FractoLabs",
  ogDescription:
    "While you're on the roof, your Digital assistant qualifies leads and books inspections in seconds — 24/7.",
  ogImage: "/images/thumbnail_roofing.png",
  ogImageAlt: "FractoLabs Roofing Interceptor Digital assistant",
  robots: "index, follow",
};

const PARTNER_META = {
  title: "FractoLabs Partner | Scale Agency MRR — White-Label Digital Assistants",
  description:
    "White-label Digital Assistant infrastructure for marketing agencies. Scale MRR without new hires — deploy in 72 hours, bundle into retainers, and keep $150–$400+ margin per client.",
  ogTitle: "Scale Your Agency MRR | FractoLabs Partner",
  ogDescription:
    "Add $300+ monthly margin per client with enterprise Digital Assistants your agency white-labels and resells — no new account managers required.",
  ogImage: "/images/logo.png",
  ogImageAlt: "FractoLabs Partner — White-Label Digital Assistants for Agencies",
  robots: "index, follow",
};

const INTERCEPT_PROTOCOL_META = {
  title: "Intercept Protocol | FractoLabs Partner",
  description:
    "If your clients are not on FractoLabs, your ad spend may be financing competitors. Lock your niche, protect campaigns, and activate $4.5k+ in monthly MRR.",
  ogTitle: "Intercept Protocol | FractoLabs Partner",
  ogDescription:
    "Lock your niche, protect ad campaigns, and flip lead leakage into white-label MRR with FractoLabs infrastructure.",
  ogImage: "/images/logo.png",
  ogImageAlt: "FractoLabs Intercept Protocol for agencies",
  robots: "index, follow",
};

const REAL_ESTATE_META = {
  title: "AI Broker for Real Estate | FractoLabs",
  description:
    "Automate WhatsApp conversations and get hot investors directly into your calendar. FractoLabs AI agent replies in 10 seconds and qualifies clients 24/7.",
  ogTitle: "AI Broker for Real Estate | FractoLabs",
  ogDescription:
    "Stop losing WhatsApp leads overnight. Get hot investors directly into your calendar with our AI broker that replies in 10 seconds.",
  ogImage: "/images/thumbnail.png",
  ogImageAlt: "FractoLabs AI Broker for Real Estate",
  robots: "index, follow",
};

const STUB_META = {
  ...STUB_SHELL_COPY,
  ogImage: "/images/logo.png",
  ogImageAlt: "",
  robots: "noindex, nofollow",
};

const ROOT_LANDING_META_BY_KEY = {
  main: DEFAULT_META,
  settrhq: DEFAULT_META,
  partner: PARTNER_META,
  roofing: ROOFING_META,
  realEstate: REAL_ESTATE_META,
  loginRedirect: STUB_META,
  stub: STUB_META,
};

const PRIVATE_ROUTE_TITLES = {
  "/dashboard": "Dashboard",
  [DASHBOARD_ROUTES.settings]: "Digital Brain Settings",
  [DASHBOARD_ROUTES.schedule]: "Digital Brain Schedule",
  "/dashboard/warm-up-sequences": "Warm-Up Sequences",
  "/dashboard/account-settings": "Account Settings",
  "/dashboard/listings": "Listings",
  "/dashboard/chat-test": "Chat Test",
  "/dashboard/chat-whatsapp": "WhatsApp Chat",
  "/dashboard/chat-telegram": "Telegram Chat",
  "/dashboard/chat-ghl": "GoHighLevel Chat",
  "/dashboard/chat-web": WEBSITE_LEAD_INTERCEPTOR_COPY.chatsPageTitle,
  "/dashboard/integrations/messengers/whatsapp": "WhatsApp Integration",
  "/dashboard/integrations/messengers/telegram": "Telegram Integration",
  "/dashboard/integrations/calendars/google": "Google Calendar",
  "/dashboard/integrations/crm/ghl": "GoHighLevel CRM",
  "/dashboard/integrations/web-widget": WEBSITE_LEAD_INTERCEPTOR_COPY.productName,
  "/dashboard/notifications": "Notifications",
  "/dashboard/billing": "Billing",
  "/partner/dashboard": "Partner Overview",
  "/partner/tiers": "Tiers & Margin",
  "/partner/deploy": "Deploy Client",
  "/partner/clients": "Clients",
  "/partner/billing": "Partner Billing",
  "/partner/settings": "Partner Settings",
  "/login": "Login",
  "/password-reset": "Password Reset",
};

const LEGAL_ROUTE_TITLES = {
  "/contact": "Contact",
  "/privacy-policy": "Privacy Policy",
  "/terms": "Terms of Service",
  "/dpa": "Data Processing Agreement",
};

const LANDING_META_BY_PATH = {
  [LANDING_PATHS.main]: DEFAULT_META,
  [LANDING_PATHS.mainPreview]: DEFAULT_META,
  [LANDING_PATHS.partner]: PARTNER_META,
  [LANDING_PATHS.roofing]: ROOFING_META,
  [LANDING_PATHS.realEstate]: REAL_ESTATE_META,
  [LANDING_PATHS.interceptProtocol]: INTERCEPT_PROTOCOL_META,
};

const resolveLandingMeta = (pathname) => LANDING_META_BY_PATH[pathname] || DEFAULT_META;

const DEFAULT_LOGO_SRC = "/images/logo.png";

/** Swap the brand token in text fields and the default logo for a known brand. */
function applyBrandToMeta(meta, brand) {
  const next = {};
  for (const [key, value] of Object.entries(meta)) {
    next[key] = typeof value === "string" ? value.replaceAll("FractoLabs", brand.name) : value;
  }
  next.siteName = brand.name;
  next.ogLocale = brand.ogLocale;
  if (meta.ogImage === DEFAULT_LOGO_SRC) {
    next.ogImage = brand.logo.default;
  }
  return next;
}

const titledMeta = (pageName, brand, robots) => ({
  ...DEFAULT_META,
  title: `${pageName} | ${brand.name}`,
  description: DEFAULT_META.description,
  ogTitle: `${pageName} | ${brand.name}`,
  robots,
});

const resolvePageMeta = (pathname, search, hostname, brand) => {
  if (pathname === "/login" || pathname === "/password-reset") {
    return titledMeta(PRIVATE_ROUTE_TITLES[pathname], brand, "noindex, nofollow");
  }

  if (pathname.startsWith("/dashboard") || pathname.startsWith("/partner")) {
    const pageName = PRIVATE_ROUTE_TITLES[pathname] || "Partner Dashboard";
    return titledMeta(pageName, brand, "noindex, nofollow");
  }

  if (LEGAL_ROUTE_TITLES[pathname]) {
    return titledMeta(LEGAL_ROUTE_TITLES[pathname], brand, "index, follow");
  }

  if (pathname === "/") {
    const key = resolveRootLandingKey(hostname, search, getStoredLeadId());
    return ROOT_LANDING_META_BY_KEY[key] ?? STUB_META;
  }

  if (LANDING_META_BY_PATH[pathname]) {
    return resolveLandingMeta(pathname);
  }

  return DEFAULT_META;
};

const STAGING_HOSTS = new Set(["staging.fractolabs.com", "staging.settrhq.com"]);
const JSONLD_PATHS = new Set(["/", "/contact", "/terms", "/privacy-policy", "/dpa"]);

export const getPageMeta = (pathname, search = "", hostname = getHostname()) => {
  const brand = getSiteBrand(hostname, search);
  if (!brand) {
    return {
      ...STUB_META,
      ogImage: "",
      ogImageAlt: "",
      applicationName: "",
      canonicalOrigin: "",
      preserveCanonical: false,
      jsonLd: "",
    };
  }

  const meta = applyBrandToMeta(resolvePageMeta(pathname, search, hostname, brand), brand);
  meta.applicationName = brand.name;
  meta.canonicalOrigin = brand.canonicalOrigin;
  if (STAGING_HOSTS.has(normalizeHostname(hostname))) {
    meta.robots = "noindex, nofollow";
    meta.preserveCanonical = true;
  }
  meta.jsonLd = JSONLD_PATHS.has(pathname) ? buildBrandJsonLd(brand) : "";
  return meta;
};

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const resolveOgImage = (ogImage, canonicalOrigin) => {
  if (!ogImage) return "";
  if (ogImage.startsWith("http")) return ogImage;
  if (canonicalOrigin) return absoluteBrandUrl(canonicalOrigin, ogImage);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${ogImage}`;
};

export const applyPageMeta = (meta) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const canonicalUrl = absoluteBrandUrl(meta.canonicalOrigin, pathname);
  const ogImage = resolveOgImage(meta.ogImage, meta.canonicalOrigin);

  document.title = meta.title;

  upsertMeta("name", "description", meta.description);
  upsertMeta("name", "application-name", meta.applicationName);
  upsertMeta("name", "robots", meta.robots);
  upsertMeta("property", "og:site_name", meta.siteName);
  upsertMeta("property", "og:title", meta.ogTitle);
  upsertMeta("property", "og:description", meta.ogDescription);
  upsertMeta("property", "og:type", "website");
  if (canonicalUrl) upsertMeta("property", "og:url", canonicalUrl);
  if (ogImage) {
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:alt", meta.ogImageAlt);
    upsertMeta("name", "twitter:image", ogImage);
  }
  upsertMeta("property", "og:locale", meta.ogLocale || "en_US");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", meta.ogTitle);
  upsertMeta("name", "twitter:description", meta.ogDescription);

  if (meta.robots?.includes("noindex") && meta.preserveCanonical !== true) {
    document.querySelector('link[rel="canonical"]')?.remove();
  } else if (meta.preserveCanonical === true && canonicalUrl) {
    upsertCanonical(canonicalUrl);
  } else if (!meta.robots?.includes("noindex")) {
    upsertCanonical(canonicalUrl);
  }

  const existingJsonLd = document.getElementById("brand-jsonld");
  if (!meta.jsonLd) {
    existingJsonLd?.remove();
    return;
  }
  const jsonLdScript = existingJsonLd || document.createElement("script");
  if (!existingJsonLd) {
    jsonLdScript.id = "brand-jsonld";
    jsonLdScript.type = "application/ld+json";
    document.head.appendChild(jsonLdScript);
  }
  jsonLdScript.textContent = meta.jsonLd;
};
